/**
 * getUserCountryCode - Fetches the user's country code based on their IP address.
 *
 * This function makes a request to the ipinfo.io API to retrieve the country code
 * associated with the user's IP. It checks if the retrieved country code is in the
 * list of valid countries (Canada, Great Britain, Australia, New Zealand, and the
 * United States). If the country code is valid, it returns that code; otherwise,
 * it defaults to 'US'. In case of an error during the fetch operation, it also
 * defaults to 'US'.
 *
 * @returns {Promise<string>} - A promise that resolves to the user's country code
 *                              or 'US' if the country code is invalid or an error occurs.
 */
const countryCodeCache = {};

export const getUserCountryCode = async () => {
  if (countryCodeCache.code) {
    return countryCodeCache.code;
  }

  try {
    const token = process.env.GATSBY_API_INFO_TOKEN;
    const res = await fetch(`https://ipinfo.io/json?token=${token}`);
    const data = await res.json();

    // Countries that use the Euro
    const euroCountries = [
      'AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE',
      'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
      'PT', 'SK', 'SI', 'ES', 'XK', 'ME'
    ];

    const gbpCountries = ['GB'];

    const validCountries = ['CA', 'AU', 'NZ', 'US'];

    if (gbpCountries.includes(data.country)) {
      countryCodeCache.code = 'GB';
    } else if (euroCountries.includes(data.country)) {
      countryCodeCache.code = 'DE';
    } else if (validCountries.includes(data.country)) {
      countryCodeCache.code = data.country;
    } else {
      countryCodeCache.code = 'US';
    }

    return countryCodeCache.code;
  } catch {
    countryCodeCache.code = 'US';
    return countryCodeCache.code;
  }
};
